import { isValidGstin, normaliseGstin } from '@oneapp/gst';
import { z } from 'zod';
import { InvitePhoneSchema } from './api';

/**
 * Privacy and data rights (Phase 17): what EkApp holds about the person, downloading it, deleting the
 * account after a grace period, asking about their data or complaining, and (for admins) telling people
 * about a breach.
 *
 *   GET    /api/v1/me/data-summary                       -> DataSummary              D2
 *   POST   /api/v1/me/exports                            -> DataExportResponse        D3: one at a time
 *   GET    /api/v1/me/exports/latest                     -> DataExportResponse        D3, D4
 *   GET    /api/v1/me/exports/:id/link                   -> DataExportLink            D4: 5 minutes
 *   DELETE /api/v1/me/exports/:id                        -> 204                       D4: delete it now
 *   GET    /api/v1/me/deletion                           -> DeletionPreview           D5
 *   DELETE /api/v1/me             DeleteAccount           -> DeletionPreview           D5: set to be deleted
 *   POST   /api/v1/me/deletion/cancel                    -> MeResponse                D7: keep my account
 *   DELETE /api/v1/me/conversations                      -> DeletedChats              D8
 *   DELETE /api/v1/me/consents/:id                       -> ConsentList               D1: withdraw
 *   POST   /api/v1/me/privacy-requests  CreatePrivacyRequest -> PrivacyRequestResponse D10
 *   GET    /api/v1/me/privacy-requests                   -> PrivacyRequestList        D10
 *   GET    /api/v1/admin/privacy-requests                -> PrivacyRequestList        (admin)
 *   POST   /api/v1/admin/privacy-requests/:id/answer  AnswerPrivacyRequest -> PrivacyRequestResponse (admin)
 *   POST   /api/v1/admin/breach-notices/preview  BreachAudience -> BreachPreview  (admin, fresh sign-in)
 *   POST   /api/v1/admin/breach-notices  BreachNotice    -> BreachNoticeResult        (admin, fresh sign-in)
 *   POST   /api/v1/admin/account-deletions/run           -> AccountDeletionsRun       (admin: testing)
 */

/** How long an account waits before it's deleted (the default; ACCOUNT_DELETION_GRACE_DAYS). */
export const DELETION_GRACE_DAYS = 7;

/** "rahul.••@gmail.com": enough to recognise, not to copy (D2, and the goodbye page, D6). */
export function maskEmail(email: string | null): string | null {
  if (!email) return null;
  const [local = '', domain = ''] = email.split('@');
  return `${local.slice(0, Math.min(6, Math.max(1, local.length - 2)))}••@${domain}`;
}

// ---------------------------------------------------------------------------
// What EkApp holds (D2)
// ---------------------------------------------------------------------------

export const PROCESSORS = ['oci', 'firebase', 'openai', 'gsp', 'sentry'] as const;
export type Processor = (typeof PROCESSORS)[number];

export const DataSummarySchema = z.object({
  asOf: z.string(),
  you: z.object({
    phoneMasked: z.string(),
    name: z.string().nullable(),
    /** Masked: "rahul.s••@gmail.com". */
    emailMasked: z.string().nullable(),
    chatLanguage: z.enum(['en', 'hi']),
    joinedAt: z.string(),
  }),
  businesses: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      role: z.enum(['owner', 'staff']).nullable(),
      /** Who else sees its returns (people in it), by name. */
      others: z.array(z.object({ name: z.string(), role: z.enum(['owner', 'staff']) })),
    }),
  ),
  kept: z.object({
    chats: z.number().int(),
    messages: z.number().int(),
    sales: z.number().int(),
    returns: z.number().int(),
    filings: z.number().int(),
    challans: z.number().int(),
    files: z.number().int(),
    /** When the oldest file still kept goes (90 days after it came), unless it's part of a filed return. */
    filesUntil: z.string().nullable(),
    statementPayments: z.number().int(),
    notices: z.number().int(),
    requests: z.number().int(),
  }),
  /** Who handles it for EkApp: the order and words are the page's. */
  processors: z.array(z.enum(PROCESSORS)),
});
export type DataSummary = z.infer<typeof DataSummarySchema>;

// ---------------------------------------------------------------------------
// Download my data (D3, D4)
// ---------------------------------------------------------------------------

export const DATA_EXPORT_STATUSES = ['queued', 'building', 'ready', 'failed', 'expired'] as const;
/** The parts of a download (privacy/manifest.ts on the server says which table goes in which). */
export const EXPORT_PARTS = [
  'profile',
  'consents',
  'businesses',
  'returns',
  'sales',
  'statements',
  'filings',
  'challans',
  'records',
  'approvals',
  'chats',
  'notices',
  'files',
  'requests',
  'activity',
] as const;
export type ExportPart = (typeof EXPORT_PARTS)[number];

export const DataExportSchema = z.object({
  id: z.string(),
  status: z.enum(DATA_EXPORT_STATUSES),
  createdAt: z.string(),
  readyAt: z.string().nullable(),
  /** When the file is deleted (a day after it's ready). */
  expiresAt: z.string().nullable(),
  bytes: z.number().int().nullable(),
  fileName: z.string(),
  /** What's in it, by part. */
  counts: z.partialRecord(z.enum(EXPORT_PARTS), z.number().int()),
});
export type DataExport = z.infer<typeof DataExportSchema>;

export const DataExportResponseSchema = z.object({ export: DataExportSchema.nullable() });
export type DataExportResponse = z.infer<typeof DataExportResponseSchema>;

export const DataExportLinkSchema = z.object({ url: z.string(), fileName: z.string(), expiresAt: z.string() });
export type DataExportLink = z.infer<typeof DataExportLinkSchema>;

// ---------------------------------------------------------------------------
// Delete my account (D5–D7)
// ---------------------------------------------------------------------------

export const DeleteAccountSchema = z.strictObject({
  /** Typed by the person: "DELETE" (any case, trimmed). */
  confirm: z
    .string()
    .trim()
    .refine((v) => v.toUpperCase() === 'DELETE', { error: 'Type DELETE to confirm' }),
});
export type DeleteAccount = z.input<typeof DeleteAccountSchema>;

export const DeletionPreviewSchema = z.object({
  /** Set once it's been asked for. */
  deletion: z.object({ requestedAt: z.string(), dueAt: z.string() }).nullable(),
  /** When it would go if asked for now. */
  dueIfNow: z.string(),
  graceDays: z.number().int(),
  businesses: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      /** Others in it keep its records (and their names); otherwise its records go with the account. */
      shared: z.boolean(),
      others: z.array(z.string()),
      /** Only staff are left in it: someone has to connect it to the GST portal to file again. */
      staffOnly: z.boolean(),
    }),
  ),
});
export type DeletionPreview = z.infer<typeof DeletionPreviewSchema>;

export const DeletedChatsSchema = z.object({ chats: z.number().int(), messages: z.number().int() });

/** Accounts whose day had come: deleted, or waiting for a filing of theirs to finish. */
export const AccountDeletionsRunSchema = z.object({ deleted: z.number().int(), waiting: z.number().int() });
export type DeletedChats = z.infer<typeof DeletedChatsSchema>;

// ---------------------------------------------------------------------------
// Asking about your data, or complaining (D10)
// ---------------------------------------------------------------------------

export const PRIVACY_TOPICS = ['access', 'correct', 'erase', 'nominate', 'complaint'] as const;
export type PrivacyTopic = (typeof PRIVACY_TOPICS)[number];

/** How long the Grievance Officer has to answer (the IT Rules: resolved within 7 days). */
export const PRIVACY_ANSWER_DAYS = 7;

export const CreatePrivacyRequestSchema = z.strictObject({
  topic: z.enum(PRIVACY_TOPICS),
  message: z
    .string()
    .trim()
    .min(1, { error: 'Tell us what it’s about' })
    .max(1000, { error: 'Keep it under 1,000 characters' }),
});
export type CreatePrivacyRequest = z.input<typeof CreatePrivacyRequestSchema>;

export const PrivacyRequestSchema = z.object({
  id: z.string(),
  ref: z.string(),
  topic: z.enum(PRIVACY_TOPICS),
  message: z.string(),
  status: z.enum(['open', 'answered']),
  createdAt: z.string(),
  dueAt: z.string(),
  answer: z.string().nullable(),
  answeredAt: z.string().nullable(),
});
export type PrivacyRequest = z.infer<typeof PrivacyRequestSchema>;

export const PrivacyRequestResponseSchema = z.object({ request: PrivacyRequestSchema });
export const PrivacyRequestListSchema = z.object({ requests: z.array(PrivacyRequestSchema) });
export type PrivacyRequestList = z.infer<typeof PrivacyRequestListSchema>;

/** The Grievance Officer's answer: to the person, in the bell and by email. */
export const AnswerPrivacyRequestSchema = z.strictObject({
  answer: z.string().trim().min(1).max(4000),
});

/** Admin list: who asked is shown masked. */
export const AdminPrivacyRequestSchema = PrivacyRequestSchema.extend({
  user: z.object({ id: z.string(), name: z.string().nullable(), phoneMasked: z.string() }),
});
export const AdminPrivacyRequestListSchema = z.object({ requests: z.array(AdminPrivacyRequestSchema) });

// ---------------------------------------------------------------------------
// A personal data breach (admin)
// ---------------------------------------------------------------------------

/**
 * Telling the people affected (the DPDP Rules' Rule 7 from May 2027; good practice now): what happened,
 * the likely impact, what EkApp did, what they can do, and who to contact. CERT-In is told separately,
 * within 6 hours (the runbook).
 */
const breachWho = {
  /** Who: these people, everyone in these businesses, or everyone. */
  userIds: z.array(z.uuid()).max(10_000).optional(),
  businessIds: z.array(z.uuid()).max(10_000).optional(),
  /** These people, by mobile (X5); and everyone in these businesses, by GSTIN. */
  phones: z.array(InvitePhoneSchema).max(1000).optional(),
  gstins: z
    .array(
      z
        .string()
        .trim()
        .transform((raw) => normaliseGstin(raw))
        .refine(isValidGstin, { error: 'Not a valid GSTIN' }),
    )
    .max(1000)
    .optional(),
  everyone: z.boolean().optional(),
};

/** Who a breach notice would reach, before it's sent (X5's "Send to 23 people"). */
export const BreachAudienceSchema = z.strictObject(breachWho);
export type BreachAudience = z.input<typeof BreachAudienceSchema>;

export const BreachPreviewSchema = z.object({
  people: z.number().int(),
  /** Mobiles with no EkApp account, and GSTINs no one in EkApp has. */
  unknownPhones: z.array(z.string()),
  unknownGstins: z.array(z.string()),
});
export type BreachPreview = z.infer<typeof BreachPreviewSchema>;

export const BreachNoticeSchema = z.strictObject({
  ...breachWho,
  whatHappened: z.string().trim().min(1).max(2000),
  impact: z.string().trim().min(1).max(2000),
  done: z.string().trim().min(1).max(2000),
  steps: z.string().trim().min(1).max(2000),
});
export type BreachNotice = z.input<typeof BreachNoticeSchema>;

export const BreachNoticeResultSchema = z.object({ notified: z.number().int() });

// ---------------------------------------------------------------------------
// Who to contact (the privacy notice, D1, D10)
// ---------------------------------------------------------------------------

export const PrivacyContactSchema = z.object({
  /** Who runs EkApp (the data fiduciary), and where. Null until set. */
  operator: z.string().nullable(),
  address: z.string().nullable(),
  officer: z.object({ name: z.string().nullable(), email: z.string().nullable() }),
  /** Days the Grievance Officer has to answer. */
  answerDays: z.number().int(),
});
export type PrivacyContact = z.infer<typeof PrivacyContactSchema>;
